import { useMemo } from 'react';
import {
  WORKLOAD_SERIES,
  buildWorkloadChart,
} from '../../core/driver-workload';
import type { DriverWorkloadRow } from '../../core/driver-workload';

interface DriverWorkloadChartProps {
  rows: readonly DriverWorkloadRow[];
}

/**
 * The unified workload graph: one stacked bar per driver, blue for the missions
 * the board dispatched and amber for the ones somebody logged by hand.
 *
 * The component draws and nothing else. Every coordinate comes from
 * `buildWorkloadChart()` in `src/core/driver-workload.ts`, so the geometry the
 * screen paints is the geometry the verification harness executes — the same
 * rule the rest of the dashboard follows about its numbers.
 *
 * Three things it deliberately does not do:
 *
 * - **It never drops a driver.** The chart grows wider with the roster and the
 *   panel scrolls it, rather than squeezing twenty names into a phone's width
 *   until the bars are stripes. A driver on nothing is the most useful bar on
 *   the screen and must never be the one that is cut.
 * - **It never relies on colour alone.** Every bar carries its total above it, a
 *   `<title>` a hover and a screen reader both read, and the legend spells out
 *   which series is which.
 * - **It never scales to zero.** An empty board still draws its axis, because a
 *   graph that vanishes on a quiet morning reads as a broken screen.
 */
export function DriverWorkloadChart({ rows }: DriverWorkloadChartProps) {
  const chart = useMemo(() => buildWorkloadChart(rows), [rows]);

  if (rows.length === 0) {
    return (
      <p className="text-sm text-ink/60">
        אין חיילים במצבת — הוסיפו נהגים כדי לראות את פילוח העומס.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto overscroll-contain">
      <svg
        viewBox={`0 0 ${chart.width} ${chart.height}`}
        width={chart.width}
        height={chart.height}
        role="img"
        aria-label={`עומס משימות לפי נהג — ${rows.length} נהגים`}
        className="max-w-none"
      >
        {/* Gridlines and the count axis, on the right — the page reads RTL. */}
        {chart.ticks.map((tick) => (
          <g key={tick.value}>
            <line
              x1={chart.plot.x}
              x2={chart.plot.x + chart.plot.width}
              y1={tick.y}
              y2={tick.y}
              stroke="#e3e8ef"
              strokeWidth={1}
            />
            <text
              x={chart.plot.x + chart.plot.width + 6}
              y={tick.y + 4}
              fontSize={11}
              fill="#14181f"
              opacity={0.55}
            >
              {tick.value}
            </text>
          </g>
        ))}

        {chart.bars.map((bar) => (
          <g key={bar.soldierId}>
            <title>{bar.title}</title>
            {bar.segments.map((segment) => (
              <rect
                key={segment.key}
                x={bar.x}
                y={segment.y}
                width={bar.width}
                height={segment.height}
                fill={segment.color}
                rx={3}
              />
            ))}
            {/* The total sits above the stack, so a bar is readable without a
                hover — and a driver on zero still gets a legible "0". */}
            <text
              x={bar.centerX}
              y={bar.topY - 6}
              fontSize={12}
              fontWeight={700}
              textAnchor="middle"
              fill="#14181f"
            >
              {bar.total}
            </text>
            {/* Names are long and the slots are narrow: rotated rather than
                truncated, because two drivers in one company can share a first
                name and an abbreviation would make the graph ambiguous. */}
            <text
              x={bar.centerX}
              y={chart.plot.y + chart.plot.height + 12}
              fontSize={11}
              fill="#14181f"
              opacity={0.75}
              textAnchor="end"
              transform={`rotate(-45 ${bar.centerX} ${chart.plot.y + chart.plot.height + 12})`}
            >
              {bar.name}
            </text>
          </g>
        ))}

        {/* The baseline, drawn last so no bar sits on top of it. */}
        <line
          x1={chart.plot.x}
          x2={chart.plot.x + chart.plot.width}
          y1={chart.plot.bottom}
          y2={chart.plot.bottom}
          stroke="#14181f"
          strokeWidth={1.5}
        />
      </svg>

      <ul className="flex flex-wrap items-center gap-4 mt-2 text-xs text-ink/70">
        {WORKLOAD_SERIES.map((series) => (
          <li key={series.key} className="flex items-center gap-1.5">
            <span
              className="w-3 h-3 rounded-sm shrink-0"
              style={{ backgroundColor: series.color }}
              aria-hidden
            />
            {series.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
